import { useEffect, useState } from "react";

export type DecodedAudio = {
  status: "idle" | "loading" | "ready" | "failed";
  buffer: AudioBuffer | null;
};

const IDLE: DecodedAudio = { status: "idle", buffer: null };

/**
 * Downloads and decodes an audio file into an AudioBuffer, for drawing a
 * waveform when there are no precomputed peaks to draw from.
 *
 * This is the expensive path peaks exist to avoid — it fetches the whole
 * track — so callers pass `undefined` whenever peaks are usable and the hook
 * then does nothing. It is the pre-redesign player's behaviour, kept as the
 * fallback for files whose peaks are pending, failed, or were never made.
 *
 * Decodes with an OfflineAudioContext rather than an AudioContext: decoding
 * needs no output device, and an AudioContext created outside a user
 * gesture starts suspended and draws an autoplay warning in some browsers.
 */
export const useDecodedAudio = (url?: string): DecodedAudio => {
  const [decoded, setDecoded] = useState<DecodedAudio>(IDLE);

  useEffect(() => {
    if (!url) {
      setDecoded(IDLE);
      return;
    }

    // Scoped to this run, so a slow decode for a previous URL can never
    // overwrite the result for the current one.
    let cancelled = false;
    setDecoded({ status: "loading", buffer: null });

    fetch(url)
      .then((response) => {
        if (!response.ok)
          throw new Error(`Audio fetch failed: ${response.status}`);
        return response.arrayBuffer();
      })
      .then((data) => {
        if (typeof OfflineAudioContext === "undefined")
          throw new Error("Web Audio is unavailable");
        // The constructor's arguments only size its render target, which
        // decoding never uses; the decoded buffer keeps the file's own
        // sample rate and length.
        return new OfflineAudioContext(1, 1, 44100).decodeAudioData(data);
      })
      .then((buffer) => {
        if (!cancelled) setDecoded({ status: "ready", buffer });
      })
      .catch(() => {
        if (!cancelled) setDecoded({ status: "failed", buffer: null });
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return decoded;
};
